import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Clock, Eye, Heart, ChevronDown } from 'lucide-react';
import { dramaApi, DramaScript } from '../services/api';

const ExplorePage: React.FC = () => {
  const [dramas, setDramas] = useState<DramaScript[]>([]);
  const [filteredDramas, setFilteredDramas] = useState<DramaScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('全部');
  const [sortBy, setSortBy] = useState('热门');
  const [showFilters, setShowFilters] = useState(false);

  const genres = ['全部', '科幻', '爱情', '悬疑', '古装', '现代', '喜剧', '励志'];
  const sortOptions = ['热门', '最新', '评分', '时长'];

  useEffect(() => {
    loadDramas();
  }, []);

  useEffect(() => {
    filterAndSortDramas();
  }, [dramas, searchQuery, selectedGenre, sortBy]);

  const loadDramas = async () => {
    try {
      setLoading(true);
      const data = await dramaApi.getAllDramas();
      setDramas(data);
    } catch (error) {
      console.error('加载短剧失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortDramas = () => {
    let filtered = [...dramas];

    // 搜索过滤
    if (searchQuery) {
      filtered = filtered.filter(drama =>
        drama.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        drama.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        drama.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // 类型过滤
    if (selectedGenre !== '全部') {
      filtered = filtered.filter(drama => drama.genre === selectedGenre);
    }

    // 排序
    switch (sortBy) {
      case '热门':
        filtered.sort((a, b) => b.views - a.views);
        break;
      case '最新':
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case '评分':
        filtered.sort((a, b) => b.likes - a.likes);
        break;
      case '时长':
        filtered.sort((a, b) => b.duration - a.duration);
        break;
    }

    setFilteredDramas(filtered);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}分${secs}秒` : `${mins}分钟`;
  };

  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">正在加载短剧...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition">
              <span className="text-xl">←</span>
              <span>返回首页</span>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">探索短剧</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* 搜索和过滤器 */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* 搜索框 */}
          <div className="mb-4">
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="搜索短剧名称、描述或标签..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
          </div>

          {/* 过滤器 */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* 类型筛选 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">类型:</span>
                <div className="flex flex-wrap gap-2">
                  {genres.map((genre) => (
                    <button
                      key={genre}
                      onClick={() => setSelectedGenre(genre)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                        selectedGenre === genre
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              {/* 排序选项 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">排序:</span>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  >
                    {sortOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* 移动端过滤器按钮 */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg"
            >
              <Filter className="w-4 h-4" />
              <span>筛选</span>
            </button>
          </div>
        </div>
      </div>

      {/* 结果统计 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <p className="text-gray-600">
            找到 <span className="font-semibold text-purple-600">{filteredDramas.length}</span> 个短剧
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-purple-600 hover:text-purple-700 text-sm"
            >
              清除搜索
            </button>
          )}
        </div>
      </div>

      {/* 短剧网格 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {filteredDramas.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">没有找到相关短剧</h3>
            <p className="text-gray-600 mb-4">尝试调整搜索关键词或筛选条件</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedGenre('全部');
                setSortBy('热门');
              }}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredDramas.map((drama) => (
              <div
                key={drama.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                {/* 封面图 */}
                <div className="relative aspect-video bg-gradient-to-br from-purple-600 to-purple-800 overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition">
                        <span className="text-xl">▶️</span>
                      </div>
                      <p className="text-sm font-medium">立即观看</p>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
                    {formatDuration(drama.duration)}
                  </div>
                  <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-full text-xs">
                    {drama.genre}
                  </div>
                </div>

                {/* 内容信息 */}
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-purple-600 transition">
                    {drama.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {drama.description}
                  </p>

                  {/* 标签 */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {drama.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                    {drama.tags.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        +{drama.tags.length - 2}
                      </span>
                    )}
                  </div>

                  {/* 统计信息 */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Eye className="w-3 h-3" />
                        <span>{formatViews(drama.views)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Heart className="w-3 h-3" />
                        <span>{drama.likes}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{new Date(drama.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* 观看按钮 */}
                  <Link
                    to={`/player/${drama.id}`}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 rounded-lg transition text-center block"
                  >
                    开始观看
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 加载更多 (如果需要) */}
      {filteredDramas.length >= 8 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="text-center">
            <button className="px-8 py-3 bg-purple-100 text-purple-600 font-medium rounded-lg hover:bg-purple-200 transition">
              加载更多短剧
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePage;